import { dictionaryApi } from './dictionaryClient';
import type { Language } from './languageApi';

export type TingantaArtha = {
  id: number;
  tingantaId: number;
  languageId: number;
  text: string;
  createdAt?: string;
  updatedAt?: string;
  created_at?: string;
  updated_at?: string;
  language?: Language | null;
};

export type CreateTingantaArthaBody = {
  tingantaId: number;
  languageId: number;
  text: string;
};

export type PatchTingantaArthaBody = Partial<Pick<TingantaArtha, 'text' | 'languageId'>>;

function normalizeList(data: unknown): TingantaArtha[] {
  if (Array.isArray(data)) return data as TingantaArtha[];
  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: TingantaArtha[] }).data;
  }
  return [];
}

export function isTingantaArtha(x: unknown): x is TingantaArtha {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.id === 'number' &&
    typeof o.tingantaId === 'number' &&
    typeof o.languageId === 'number' &&
    typeof o.text === 'string'
  );
}

export function parseTingantaArthas(raw: unknown[] | undefined): TingantaArtha[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isTingantaArtha);
}

export async function listTingantaArthas(tingantaId?: number): Promise<TingantaArtha[]> {
  const res = await dictionaryApi.get<unknown>('/tinganta-artha', {
    params: tingantaId != null ? { tingantaId } : undefined,
  });
  return normalizeList(res.data);
}

export async function getTingantaArtha(id: number): Promise<TingantaArtha> {
  const res = await dictionaryApi.get<TingantaArtha>(`/tinganta-artha/${id}`);
  return res.data;
}

export async function createTingantaArtha(body: CreateTingantaArthaBody): Promise<TingantaArtha> {
  const res = await dictionaryApi.post<TingantaArtha>('/tinganta-artha', body);
  return res.data;
}

export async function updateTingantaArtha(
  id: number,
  body: PatchTingantaArthaBody
): Promise<TingantaArtha> {
  const res = await dictionaryApi.patch<TingantaArtha>(`/tinganta-artha/${id}`, body);
  return res.data;
}

export async function deleteTingantaArtha(id: number): Promise<void> {
  await dictionaryApi.delete(`/tinganta-artha/${id}`);
}

export function buildTingantaArthaPatch(
  prev: TingantaArtha,
  next: Pick<TingantaArtha, 'text' | 'languageId'>
): PatchTingantaArthaBody {
  const patch: PatchTingantaArthaBody = {};
  if (prev.languageId !== next.languageId) patch.languageId = next.languageId;
  if (prev.text !== next.text) patch.text = next.text.trim();
  return patch;
}
